import React, { useState, useEffect } from 'react';
import { Bell, Send, Loader2, Smartphone, Mail, Users, Car, RefreshCw, CheckCircle, XCircle, Clock, Settings, MessageSquare, FileText } from 'lucide-react';
import { api } from '../../services/api';

export function AdminNotificationsTab() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [form, setForm] = useState({
    recipients: 'all_drivers',
    title: '',
    body: '',
    send_push: true,
    send_email: false,
  });
  const [result, setResult] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsR, logsR, settingsR] = await Promise.all([
        api.get('/admin/notifications-stats').catch(() => ({ data: null })),
        api.get('/admin/notifications-log').catch(() => ({ data: [] })),
        api.get('/admin/notification-settings').catch(() => ({ data: null })),
      ]);
      setStats(statsR.data);
      setLogs(logsR.data || []);
      setSettings(settingsR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!form.title || !form.body) return;
    setSending(true);
    setResult(null);
    try {
      const res = await api.post('/admin/send-notification', form);
      setResult({ success: true, message: res.data.message });
      setForm(f => ({ ...f, title: '', body: '' }));
      loadData();
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.detail || 'Fehler beim Senden' });
    } finally { setSending(false); }
  };

  const toggleSetting = async (key) => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const res = await api.put('/admin/notification-settings', { [key]: !settings[key] });
      setSettings(res.data);
    } catch (e) { console.error(e); }
    finally { setSavingSettings(false); }
  };

  const recipientLabels = {
    all_drivers: 'Alle Fahrer',
    all_customers: 'Alle Kunden',
    all_users: 'Alle Benutzer',
    custom: 'Benutzerdefiniert',
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-tab">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="notification-stats">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <Smartphone size={16} className="text-blue-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Geräte gesamt</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-total-devices">{stats.total_subscribed_devices}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <Car size={16} className="text-green-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Fahrer-Geräte</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-driver-devices">{stats.driver_devices}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users size={16} className="text-purple-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Kunden-Geräte</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-customer-devices">{stats.customer_devices}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                <Bell size={16} className="text-orange-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Gesendet</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-total-sent">{stats.total_notifications_sent}</p>
          </div>
        </div>
      )}

      {/* Notification Channel Settings */}
      {settings && (
        <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="notification-channel-settings">
          <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Settings size={18} className="text-slate-500" /> Benachrichtigungs-Kanäle
          </h2>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 mb-2">Aktivieren/deaktivieren Sie die Kanäle für automatische Benachrichtigungen</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { key: 'sms_new_transfer', label: 'SMS bei neuer Anfrage', icon: MessageSquare, desc: 'Fahrer per SMS benachrichtigen' },
                { key: 'email_new_transfer', label: 'E-Mail bei neuer Anfrage', icon: Mail, desc: 'Fahrer per E-Mail benachrichtigen' },
                { key: 'push_new_transfer', label: 'Push bei neuer Anfrage', icon: Smartphone, desc: 'Push-Benachrichtigung an Fahrer' },
                { key: 'sms_booking_confirmed', label: 'SMS bei Buchung', icon: MessageSquare, desc: 'Bestätigung per SMS' },
                { key: 'email_booking_confirmed', label: 'E-Mail bei Buchung', icon: Mail, desc: 'Bestätigung per E-Mail' },
                { key: 'push_booking_confirmed', label: 'Push bei Buchung', icon: Smartphone, desc: 'Bestätigung per Push' },
                { key: 'email_document_expiry', label: 'Dokument-Ablauf E-Mail', icon: FileText, desc: 'Warnung vor Ablauf' },
              ].map(ch => {
                const Icon = ch.icon;
                const isOn = settings[ch.key] !== false;
                return (
                  <div key={ch.key} className={`flex items-center justify-between p-3 rounded-lg border ${isOn ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon size={14} className={isOn ? 'text-emerald-600' : 'text-slate-400'} />
                      <div>
                        <p className="text-xs font-medium text-slate-700">{ch.label}</p>
                        <p className="text-[10px] text-slate-400">{ch.desc}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleSetting(ch.key)}
                      disabled={savingSettings}
                      className={`w-10 h-5 rounded-full relative transition-colors ${isOn ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      data-testid={`toggle-${ch.key}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${isOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Firebase Status */}
      {stats && !stats.firebase_ready && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="firebase-warning">
          Firebase ist nicht konfiguriert. Push-Benachrichtigungen sind deaktiviert. Nur E-Mail-Versand möglich.
        </div>
      )}

      {/* Send Notification Form */}
      <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="notification-send-form">
        <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Bell size={18} className="text-orange-500" /> Benachrichtigung senden
        </h2>
        <div className="space-y-4">
          {/* Recipients */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Empfänger</label>
            <select
              value={form.recipients}
              onChange={e => setForm(f => ({ ...f, recipients: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm bg-white"
              data-testid="notification-recipients-select"
            >
              <option value="all_drivers">Alle Fahrer</option>
              <option value="all_customers">Alle Kunden</option>
              <option value="all_users">Alle Benutzer</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Titel</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Benachrichtigungs-Titel"
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm"
              data-testid="notification-title-input"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Nachricht</label>
            <textarea
              value={form.body}
              onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              placeholder="Ihre Nachricht..."
              rows={4}
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm resize-none"
              data-testid="notification-body-textarea"
            />
          </div>

          {/* Channels */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Kanäle</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_push}
                  onChange={e => setForm(f => ({ ...f, send_push: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  data-testid="notification-push-checkbox"
                />
                <Smartphone size={14} className="text-slate-500" />
                <span className="text-sm text-slate-700">Push-Benachrichtigung</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  data-testid="notification-email-checkbox"
                />
                <Mail size={14} className="text-slate-500" />
                <span className="text-sm text-slate-700">Auch per E-Mail</span>
              </label>
            </div>
          </div>

          {/* Result message */}
          {result && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${result.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="notification-result">
              {result.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {result.message}
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={sending || !form.title || !form.body || (!form.send_push && !form.send_email)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            data-testid="notification-send-btn"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Benachrichtigung senden
          </button>
        </div>
      </div>

      {/* Notification Log */}
      <div className="bg-white rounded-xl border border-slate-100" data-testid="notification-log">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-sm text-slate-800">Gesendete Benachrichtigungen ({logs.length})</h2>
          <button onClick={loadData} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-testid="notification-refresh-btn">
            <RefreshCw size={14} className={`text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {logs.length === 0 ? (
          <p className="p-6 text-sm text-slate-400 text-center">Noch keine Benachrichtigungen gesendet</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {logs.map(log => (
              <div key={log.id} className="px-5 py-3 hover:bg-slate-50" data-testid={`notification-log-${log.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800">{log.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{log.body}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {recipientLabels[log.recipients_group] || log.recipients_group}
                      </span>
                      {log.send_push && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Smartphone size={10} /> {log.push_success}/{log.push_recipients}
                        </span>
                      )}
                      {log.send_email && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Mail size={10} /> {log.email_recipients}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString('de-DE') : '-'}</p>
                    <p className="text-xs text-slate-500 mt-0.5">von {log.sent_by_name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
